import type {
  FormCol,
  FormLayout,
  FormLayoutItemRow
} from '@aurora/shared-generated/types/graphql-schema-types';
import type { FormatMessageValues, I18n } from '@aurora/shared-types/texts';
import { deepClone } from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import { getLog } from '@aurora/shared-utils/log';
import type { GraphQLFormattedError } from 'graphql';
import type { UseFormSetError } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import {
  FormFeedbackPosition,
  FormFeedbackType
} from '../../../helpers/form/FormFieldFeedback/enums';
import type FormFeedbackTypes from '../../../helpers/form/FormFieldFeedback/form-feedback-types';
import { ToastAlertVariant, ToastVariant } from '../../common/ToastAlert/enums';
import type ToastAlertProps from '../../common/ToastAlert/ToastAlertProps';
import type { ToastContextInterface } from '../../context/ToastContext/ToastContext';
import useToasts from '../../context/ToastContext/useToasts';
import useErrorMessages from '../useErrorMessages/useErrorMessages';

const log = getLog(module);

/**
 * The general mutation error type to display: form feedback or toast alert
 */
export enum MutationErrorType {
  ALERT = 'alert',
  FEEDBACK = 'feedback'
}

interface MutationError {
  /**
   * The message to display to the user when the error occurs
   */
  message: string;
}

export interface MutationErrorAlert extends MutationError, Pick<ToastAlertProps, 'id' | 'title'> {
  /**
   * The type of error to display in the UI
   */
  type: MutationErrorType.ALERT;
}

export interface MutationErrorFeedback extends MutationError {
  /**
   * The type of error to display in the UI
   */
  type: MutationErrorType.FEEDBACK;
}

interface UIError {
  /**
   * The generated apollo typename for the mutation error
   */
  __typename?: string;
  /**
   * The corresponding field(s) for the error
   */
  fields: Array<string>;
}

/**
 * Returns an ordered list of field names based off of the provided form layout
 *
 * @param layoutItems the layout rows or items from the FormSchema layout
 * @param result the list of field names to be returned
 */
export function normalizeLayout(
  layoutItems: Array<FormLayoutItemRow> | Array<FormCol>,
  result: Array<string> = []
): Array<string> {
  if (!layoutItems || layoutItems.length === 0) {
    return result;
  }

  layoutItems.forEach(item => {
    if ('items' in item) {
      return normalizeLayout(item.items, result);
    } else {
      result.push(item.id);
    }
  });

  return result;
}

/**
 * Determines the field that should receive focus when an error occurs
 *
 * @param knownErrors the errors returned by the mutation
 * @param processedFieldInputs the ordered list of field names from the form layout
 */
export function getTargetFieldForFocus<ErrorT extends UIError>(
  knownErrors: Array<ErrorT>,
  processedFieldInputs: Array<string>
): string {
  let targetFieldIdx: number = processedFieldInputs.length - 1;

  knownErrors.forEach((error: ErrorT) => {
    error.fields.forEach((field: string) => {
      const fieldInputIdx: number = processedFieldInputs.indexOf(field);
      if (fieldInputIdx === -1) {
        log.error(`no match between field input with id ${field} and form spec layout definition`);
      } else if (fieldInputIdx < targetFieldIdx) {
        targetFieldIdx = fieldInputIdx;
      }
    });
  });

  return processedFieldInputs[targetFieldIdx];
}

/**
 * Sets a form feedback error at the top of the form
 *
 * @param mutationErrorFeedback contains the message for the feedback
 * @param setFormFeedback sets form feedback
 */
function displayFormFeedbackError(
  mutationErrorFeedback: MutationErrorFeedback,
  setFormFeedback: (feedback: FormFeedbackTypes.FormFeedback) => void
): void {
  setFormFeedback({
    message: mutationErrorFeedback.message,
    variant: {
      type: FormFeedbackType.BANNER
    },
    position: FormFeedbackPosition.TOP
  });
}

/**
 * Displays an error alert
 *
 * @param mutationErrorAlert contains the message, title, and id for the alert
 * @param addToast adds a toast alert for the error in the UI
 */
function displayToastError(
  mutationErrorAlert: MutationErrorAlert,
  addToast: ToastContextInterface['addToast']
) {
  addToast({
    id: mutationErrorAlert.id,
    title: mutationErrorAlert.title,
    message: mutationErrorAlert.message,
    alertVariant: ToastAlertVariant.DANGER,
    toastVariant: ToastVariant.BANNER
  });
}

/**
 * Displays a single error as either form feedback or an alert
 *
 * @param mutationErrorType the error type, feedback or alert
 * @param setFormFeedback sets form feedback
 * @param addToast Adds a toast alert for the error in the UI
 */
export function displaySingleError(
  mutationErrorType: MutationErrorAlert | MutationErrorFeedback,
  setFormFeedback: (feedback: FormFeedbackTypes.FormFeedback) => void,
  addToast: ToastContextInterface['addToast']
): void {
  if (mutationErrorType.type === MutationErrorType.FEEDBACK) {
    displayFormFeedbackError(mutationErrorType, setFormFeedback);
  } else {
    displayToastError(mutationErrorType, addToast);
  }
}

/**
 * Processes mutation errors for a given form, using various methods to display feedback to the user
 * Errors may be presented as either inline errors for form field(s), or a single form feedback or alert
 * Sets focus on the first form field containing errors
 *
 * @param formId the id of the form
 * @param componentI18n the internationalization object
 * @param formLayout the form layout
 */
export default function useMutationErrorsHandler<T, R>(
  formId: string,
  componentI18n: I18n<T, R>,
  formLayout: FormLayout
) {
  const { addToast } = useToasts();
  const getMessageForFieldError = useErrorMessages(formId, componentI18n);

  /**
   * Handles the errors returned by a given mutation, displaying errors beside their respective field(s) where available
   *
   * General errors (GraphQL errors or those that do not have an associated field) will display either as a single form
   * feedback, or, an alert
   *
   * @param GraphQLFormattedErrors errors from the GraphQL mutation response {@link https://www.apollographql.com/docs/react/data/error-handling/#graphql-errors}
   * @param knownErrors known errors defined by a given schema, and/or validation errors (both front-end and backend)
   * @param setError react-hook-form setError, used to set an inline error directly below a given field
   * @param setFormFeedback sets a single form feedback
   * @param mutationErrorType the type of mutation error to display in the UI - alert, or feedback
   *
   */
  return <FormDataT, ErrorT extends UIError>(
    GraphQLFormattedErrors: readonly GraphQLFormattedError[],
    knownErrors: ErrorT[] | null,
    setError: UseFormSetError<FormDataT>,
    setFormFeedback: (feedback: FormFeedbackTypes.FormFeedback) => void,
    mutationErrorType: MutationErrorAlert | MutationErrorFeedback
  ): void => {
    // if receiving top-level GraphQL errors, display a single error as either an alert or in-form feedback
    if (GraphQLFormattedErrors?.length > 0) {
      displaySingleError(mutationErrorType, setFormFeedback, addToast);
    }
    // if receiving known server errors defined by our schemas, iterate over the errors and fields, setting inline
    //  messages on form fields where applicable
    if (knownErrors?.length > 0) {
      const targetFieldForFocus: string = getTargetFieldForFocus(
        knownErrors,
        normalizeLayout(deepClone(formLayout).rows)
      );

      knownErrors.forEach((error: ErrorT) => {
        const { fields, __typename, ...customArguments } = error;

        // some errors do not contain corresponding fields, so display a general error in this case
        if (!fields || fields.length === 0) {
          displaySingleError(mutationErrorType, setFormFeedback, addToast);
        } else {
          const fieldMessages: Array<string> = fields.map((field: string) =>
            getMessageForFieldError(__typename, field, customArguments as FormatMessageValues)
          );

          // use field-specific translations if present, otherwise use those for the specific error type
          fields.forEach((field: FieldPath<FormDataT>, fieldIdx: number) => {
            setError(
              field,
              { type: 'manual', message: fieldMessages[fieldIdx] },
              {
                shouldFocus: field === targetFieldForFocus
              }
            );
          });
        }
      });
    }
  };
}
